/**
 * RagDonut — pure-SVG donut chart for Project Health RAG breakdown.
 * No chart library dependency; just arcs computed from counts.
 *
 * Usage: <RagDonut counts={{ on_track, at_risk, delayed, on_hold, closed }} />
 */

const SLICE_ORDER = [
  { key: 'delayed',  label: 'Delayed',  var: 'var(--rag-red)' },
  { key: 'at_risk',  label: 'At risk',  var: 'var(--rag-amber)' },
  { key: 'on_track', label: 'On track', var: 'var(--rag-green)' },
  { key: 'on_hold',  label: 'On hold',  var: 'var(--neutral)' },
  { key: 'closed',   label: 'Closed',   var: 'var(--foreground-subtle)' },
];

function polarToCartesian(cx, cy, r, angleDeg) {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function arcPath(cx, cy, r, startAngle, endAngle) {
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArc = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}

export function RagDonut({ counts = {}, size = 148, thickness = 22, testId = 'rag-donut' }) {
  const slices = SLICE_ORDER.map(s => ({ ...s, count: counts[s.key] || 0 }))
                            .filter(s => s.count > 0);
  const total = slices.reduce((a, b) => a + b.count, 0);
  const cx = size / 2;
  const cy = size / 2;
  const r = (size - thickness) / 2;

  let accAngle = 0;
  const paths = slices.map((s) => {
    const start = accAngle;
    const sweep = (s.count / total) * 360;
    accAngle += sweep;
    // If a single slice is 100%, draw a full circle instead of an arc (arc math breaks at 360°).
    if (Math.abs(sweep - 360) < 0.001) {
      return {
        key: s.key,
        d: `M ${cx - r} ${cy} A ${r} ${r} 0 1 0 ${cx + r} ${cy} A ${r} ${r} 0 1 0 ${cx - r} ${cy}`,
        color: `hsl(${s.var})`,
      };
    }
    return { key: s.key, d: arcPath(cx, cy, r, start, accAngle), color: `hsl(${s.var})` };
  });

  return (
    <div className="flex items-center gap-4" data-testid={testId}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-none">
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="hsl(var(--surface-2))" strokeWidth={thickness} />
        {paths.map(p => (
          <path key={p.key} d={p.d} fill="none" stroke={p.color}
                strokeWidth={thickness} strokeLinecap="butt" />
        ))}
        <text x={cx} y={cy - 4} textAnchor="middle"
              fontFamily="var(--font-heading)" fontSize={22} fontWeight={600}
              fill="hsl(var(--foreground))">{total}</text>
        <text x={cx} y={cy + 14} textAnchor="middle"
              fontFamily="var(--font-sans)" fontSize={10.5}
              fill="hsl(var(--foreground-subtle))">projects</text>
      </svg>
      <div className="grid grid-cols-1 gap-1.5">
        {SLICE_ORDER.map(s => {
          const c = counts[s.key] || 0;
          const pct = total ? Math.round((c / total) * 100) : 0;
          return (
            <div key={s.key} className="flex items-center gap-2 text-[12px]"
                 data-testid={`rag-legend-${s.key}`}>
              <span className="inline-block w-2.5 h-2.5 rounded-sm flex-none"
                    style={{ background: `hsl(${s.var})` }} />
              <span className="min-w-[80px]">{s.label}</span>
              <span className="tabular-nums font-medium">{c}</span>
              <span className="text-[hsl(var(--foreground-subtle))] tabular-nums">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
